import React, { useState, useEffect, useMemo } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { DealQuotationLink, quotationViewPath } from './DealQuotationLink'
import { GST_STATES, resolvePlaceOfSupply, stateCodeFromGstin } from '@jira-clone/shared-types'
import { toast } from 'react-hot-toast'
import { api } from '@/api/client'
import { ConfirmDialog } from '@/shared/components/ui/ConfirmDialog'
import { getApiErrorMessage } from '@/shared/utils/apiError'
import { CalendarPickerDropdown } from './CalendarPickerDropdown'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Button,
  Input,
  Textarea,
  Badge,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
} from '@goodday-clone/ui'
import {
  BadgeDollarSign,
  Plus,
  Copy,
  Zap,
  Settings,
  MoreHorizontal,
  Bell,
  Pin,
  Maximize2,
  Minimize2,
  X,
  CheckCircle2,
  Calendar,
  Flag,
  Mail,
  Phone,
  Layers,
  Percent,
  FileText,
  Building2,
  User,
  CheckSquare,
  Square,
  Send,
  TrendingUp,
  ListTodo,
  GitMerge,
  ExternalLink,
  Activity,
  HeartPulse,
  Repeat,
  Clock,
  Pencil,
  Trash2,
  Archive,
  AlertTriangle,
  Ban,
  ChevronsUp,
  ChevronUp,
  Circle,
  ChevronDown,
  ChevronsDown,
  Link as LinkIcon,
  SlidersHorizontal,
  Check,
  Kanban,
  Eye,
  EyeOff,
  Volume2,
  VolumeX,
  ArrowDownUp,
  Rocket,
  FolderPlus,
  RefreshCw,
  MapPin,
} from 'lucide-react'
import { Deal } from '@/lib/api-client'
import { useAuth } from '@/context/AuthContext'
import { isSuperAdminOrOwner } from '@jira-clone/shared-types'
import { useCancelDeletionRequest, useDeleteCrmRecord, usePendingDeletionRequest, isDatabaseId } from '@/shared/hooks/useCrmDeletion'
import { useDealSpaceAccess } from '@/shared/hooks/useDealSpaceAccess'
import { useDealStages } from '@/shared/hooks/useCrmVocabulary'
import { useDealTodos } from '@/shared/hooks/useTodos'
import { useDealComments } from '@/shared/hooks/useComments'
import { formatDistanceToNow } from 'date-fns'
import { createProjectFromDeal, fetchLinkedProjects, LinkedProjectInfo } from '@/lib/deal-project-utils'
import { ServicesPicker } from './ServicesPicker'
import { useProductServices, ProductService } from '@/shared/hooks/useProductServices'

export interface DealDetailData extends Deal {
  priority?: string
  contactPerson?: string
  contactDesignation?: string
  contactEmail?: string
  contactPhone?: string
  source?: string
  number?: string | number
  services?: string[]
  address?: string
  billingAddress?: string
  clientAddress?: string
  gstin?: string
  clientGstin?: string
  /** The customer's state ("Karnataka (29)"): the place of supply when they have no GSTIN. */
  clientState?: string
  pinned?: boolean
  reminders?: Array<{
    _id?: string
    title: string
    dueDate?: string
    notes?: string
    completed?: boolean
    createdAt?: string
  }>
}

interface DealDetailModalProps {
  isOpen: boolean
  onClose: () => void
  deal: DealDetailData | null
  onUpdate?: (updatedDeal: DealDetailData) => void
  onDeleted?: (id: string) => void
  currentSectionName?: string
  currentSectionHref?: string
}

const API_BASE_URL = (typeof process !== 'undefined' && process.env?.NEXT_PUBLIC_API_URL) || (import.meta as any)?.env?.VITE_API_URL || 'http://localhost:4000'

export const PRIORITY_OPTIONS = [
  { label: 'Emergency', value: 'Emergency', icon: AlertTriangle, color: 'text-red-500', bg: 'bg-red-500/10 text-red-600 dark:text-red-400', border: 'border-red-500/30' },
  { label: 'Blocker', value: 'Blocker', icon: Ban, color: 'text-red-500', bg: 'bg-red-500/10 text-red-600 dark:text-red-400', border: 'border-red-500/30' },
  { label: 'Highest', value: 'Highest', icon: ChevronsUp, color: 'text-orange-500', bg: 'bg-orange-500/10 text-orange-600 dark:text-orange-400', border: 'border-orange-500/30' },
  { label: 'High', value: 'High', icon: ChevronUp, color: 'text-orange-400', bg: 'bg-orange-400/10 text-orange-600 dark:text-orange-400', border: 'border-orange-400/30' },
  { label: 'Normal', value: 'Normal', icon: Circle, color: 'text-slate-400', bg: 'bg-slate-500/10 text-slate-600 dark:text-slate-300', border: 'border-slate-500/30' },
  { label: 'Low', value: 'Low', icon: ChevronDown, color: 'text-blue-400', bg: 'bg-blue-400/10 text-blue-600 dark:text-blue-400', border: 'border-blue-400/30' },
  { label: 'Lowest', value: 'Lowest', icon: ChevronsDown, color: 'text-blue-500', bg: 'bg-blue-500/10 text-blue-600 dark:text-blue-400', border: 'border-blue-500/30' },
] as const

export function DealDetailModal({
  isOpen,
  onClose,
  deal,
  onUpdate,
  onDeleted,
  currentSectionName = 'Active Deals',
  currentSectionHref = '/app/deals',
}: DealDetailModalProps) {
  const { data: dealStages = [] } = useDealStages()
  const wonStageNames = useMemo(() => dealStages.filter((s) => s.isWon).map((s) => s.name), [dealStages])

  // Who can own a deal: Super Admin + the Sales department (same list as leads).
  const [teamMembers, setTeamMembers] = useState<{ id: string; name: string }[]>([])
  useEffect(() => {
    api
      .get<{ data: { id: string; name: string }[] }>('/users/assignable?for=leads')
      .then((res) => setTeamMembers(res?.data || []))
      .catch(() => {})
  }, [])
  const { data: masterServices = [] } = useProductServices({ isActive: true })

  // Services state (must be declared before useMemo that references it)
  const [services, setServices] = useState<string[]>(deal?.services || [])

  // Master-data service lookup and total computation
  const masterDataByName = useMemo(() => {
    const map: Record<string, ProductService> = {}
    for (const ps of masterServices) {
      if (ps.name) map[ps.name.toLowerCase()] = ps
    }
    return map
  }, [masterServices])

  const totalFromServices = useMemo(() => {
    if (!services || services.length === 0) return 0
    return services.reduce((sum, svc) => {
      const md = masterDataByName[svc.toLowerCase()]
      return sum + (md?.defaultRate || 0)
    }, 0)
  }, [services, masterDataByName])

  const formatCurrency = (amount: number) => {
    if (amount > 0) return `₹${amount.toLocaleString('en-IN')}`
    return ''
  }

  // Local Editable States
  const [title, setTitle] = useState(deal?.title || '')
  const [client, setClient] = useState(deal?.client || '')
  const [stage, setStage] = useState(deal?.stage || 'Discovery')
  const isWonStage = useMemo(() => {
    const st = (stage || deal?.stage || '').toLowerCase()
    return st === 'closed won' || wonStageNames.some((w: any) => String(w).toLowerCase() === st)
  }, [stage, deal?.stage, wonStageNames])
  // The banner below shows once the deal is won or its advance payment has been received; a space can be created for any deal.
  const canLaunchProject = isWonStage || Boolean((deal as any)?.canLaunchProject)
  const [value, setValue] = useState(deal?.value !== undefined ? String(deal.value) : '0')
  const [probability, setProbability] = useState(deal?.probability !== undefined ? String(deal.probability) : '0')
  const [owner, setOwner] = useState(deal?.owner || '')
  const [closeDate, setCloseDate] = useState(
    deal?.closeDate || new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })
  )
  const [notes, setNotes] = useState(deal?.notes || '')
  const [contactPerson, setContactPerson] = useState(deal?.contactPerson || '')
  const [contactDesignation, setContactDesignation] = useState(deal?.contactDesignation || '')
  const [contactEmail, setContactEmail] = useState(deal?.contactEmail || '')
  const [contactPhone, setContactPhone] = useState(deal?.contactPhone || '')
  const [source, setSource] = useState(deal?.source || '')
  const [clientAddress, setClientAddress] = useState(deal?.clientAddress || deal?.address || deal?.billingAddress || '')
  const [clientGstin, setClientGstin] = useState(deal?.clientGstin || deal?.gstin || '')
  const [clientState, setClientState] = useState(deal?.clientState || '')

  // Priority & Custom Fields State
  const [priority, setPriority] = useState<string>((deal as any)?.priority || 'Normal')
  const [customLinks, setCustomLinks] = useState<{ id: string; url: string; label: string }[]>([])
  const [customPages, setCustomPages] = useState<{ id: string; title: string; url?: string }[]>([
    { id: 'p1', title: 'Sales Collateral & Pricing Specs' },
  ])
  const [isAddingLink, setIsAddingLink] = useState(false)
  const [newLinkUrl, setNewLinkUrl] = useState('https://')
  const [newLinkLabel, setNewLinkLabel] = useState('')
  const [showAllFields, setShowAllFields] = useState(false)

  // Todos - real backend persistence via GoodDayTodo (see useTodos.ts)
  const { todos, createTodo, toggleTodo, deleteTodo } = useDealTodos(deal?.id)
  const [newTodo, setNewTodo] = useState('')
  const [isAddingTodo, setIsAddingTodo] = useState(false)

  // Waterfall Dependencies
  const [dependencies, setDependencies] = useState<
    { id: string; title: string; type: string; status: string }[]
  >([
    {
      id: 'dep-1',
      title: 'Technical Scope & Security Review',
      type: 'Pre-requisite',
      status: 'Completed',
    },
  ])
  const [isAddingDependency, setIsAddingDependency] = useState(false)
  const [newDepTitle, setNewDepTitle] = useState('')

  const titleInputRef = React.useRef<HTMLInputElement>(null)

  // Comments / Activity Stream - real backend persistence via the generic
  // GoodDayComment system (see useComments.ts)
  const { comments, createComment } = useDealComments(deal?.id)
  const [replyText, setReplyText] = useState('')
  const [isSaving, setIsSaving] = useState(false)
  const [saveMessage, setSaveMessage] = useState<string | null>(null)
  const [isDeleteOpen, setIsDeleteOpen] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [deleteReason, setDeleteReason] = useState('')
  const [isFullscreen, setIsFullscreen] = useState(false)

  // Settings & Form Preferences
  const [visibleSections, setVisibleSections] = useState({
    probability: true,
    todos: true,
    dependencies: true,
    customFields: true,
    crm: true,
    systemLogs: true,
  })
  const [dealData, setDealData] = useState<DealDetailData | null>(deal)
  const [pinned, setPinned] = useState(Boolean(deal?.pinned))
  const [commentSortOrder, setCommentSortOrder] = useState<'newest' | 'oldest'>('oldest')
  const [watchStatus, setWatchStatus] = useState<'watching' | 'normal' | 'muted'>('watching')
  const { organization, user: currentUser } = useAuth()
  const mayDeleteAlone = isSuperAdminOrOwner(currentUser?.role)
  const deleteRecord = useDeleteCrmRecord()
  const cancelDeleteRequest = useCancelDeletionRequest()
  const { pending: pendingDelete } = usePendingDeletionRequest(
    'deal',
    isDatabaseId(deal?.id) ? deal?.id : undefined
  )
  const { canCreateSpace, canViewSpace } = useDealSpaceAccess()
  const [linkedProject, setLinkedProject] = useState<LinkedProjectInfo | null>(null)
  const [isCreatingProject, setIsCreatingProject] = useState(false)
  const [projectBanner, setProjectBanner] = useState<string | null>(null)

  useEffect(() => {
    if (deal?.id && isOpen) {
      fetchLinkedProjects(organization?.id).then((map) => {
        setLinkedProject(map.get(deal.id) || null)
      })
    }
  }, [deal?.id, isOpen, organization?.id])

  const handleCreateSpaceProject = async () => {
    if (!deal) return
    setIsCreatingProject(true)
    try {
      const res = await createProjectFromDeal(
        {
          id: deal.id,
          title: title || deal.title,
          client: client || deal.client,
          notes: notes || deal.notes,
          value: value || deal.value,
          owner: owner || deal.owner,
          closeDate: closeDate || deal.closeDate,
          contactPerson: contactPerson || deal.contactPerson,
          contactEmail: contactEmail || deal.contactEmail,
          contactPhone: contactPhone || deal.contactPhone,
          probability: probability || deal.probability,
          services: services || deal.services || [],
          clientAddress: clientAddress || deal.clientAddress || deal.address || deal.billingAddress,
          clientGstin: clientGstin || deal.clientGstin || deal.gstin,
        },
        organization?.id
      )
      if (res.success && res.project) {
        setLinkedProject(res.project)
        setProjectBanner(`Project "${res.project.name}" [${res.project.key}] created and displayed at /spaces!`)
        applyStageChange('Closed Won')
      } else {
        if (res.error?.includes('already created') || res.error?.includes('already in use')) {
          setProjectBanner(res.error)
          fetchLinkedProjects(organization?.id).then((map) => {
            const linked = map.get(deal.id)
            if (linked) setLinkedProject(linked)
          })
          toast(res.error)
        } else {
          toast.error(res.error || 'Could not create the project.')
        }
      }
    } finally {
      setIsCreatingProject(false)
    }
  }

  useEffect(() => {
    if (deal) {
      setDealData(deal)
      setPinned(Boolean(deal.pinned))
      setTitle(deal.title || '')
      setClient(deal.client || '')
      setStage(deal.stage || 'Discovery')
      setValue(deal.value !== undefined ? String(deal.value) : '0')
      setProbability(deal.probability !== undefined ? String(deal.probability) : '0')
      setOwner(deal.owner || '')
      setCloseDate(
        deal.closeDate || new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
        })
      )
      setNotes(deal.notes || '')
      setContactPerson(deal.contactPerson || '')
      setContactDesignation(deal.contactDesignation || '')
      setContactEmail(deal.contactEmail || '')
      setContactPhone(deal.contactPhone || '')
      setSource(deal.source || '')
      setServices(deal.services || [])
      setClientAddress(deal.clientAddress || deal.address || deal.billingAddress || '')
      setClientGstin(deal.clientGstin || deal.gstin || '')
      setClientState(deal.clientState || '')
    }
  }, [deal])

  const navigate = useNavigate()

  const handleCreateQuotation = (dealItem?: DealDetailData | null) => {
    const targetDeal = dealItem || deal
    if (!targetDeal) return

    let addr = clientAddress || targetDeal.clientAddress || targetDeal.address || targetDeal.billingAddress || ''
    let gst = clientGstin || targetDeal.clientGstin || targetDeal.gstin || ''
    if (!addr && (notes || targetDeal.notes)) {
      const noteText = notes || targetDeal.notes || ''
      const am = noteText.match(/(?:Address|Billing Address|Location):\s*([^,\n\)]+)/i)
      if (am) addr = am[1].trim()
    }
    if (!gst && (notes || targetDeal.notes)) {
      const noteText = notes || targetDeal.notes || ''
      const gm = noteText.match(/(?:GSTIN|GST|Tax ID):\s*([A-Z0-9]{15})/i)
      if (gm) gst = gm[1].trim()
    }

    const val = typeof value === 'string' ? parseFloat(value) || 0 : (targetDeal.value || 0)

    const payload = {
      dealId: targetDeal.id,
      title: title || targetDeal.title,
      clientName: client || targetDeal.client,
      clientAddress: addr,
      clientGstin: gst,
      clientState: resolvePlaceOfSupply({ customerGstin: gst, customerState: clientState || targetDeal.clientState }).label,
      contactPerson: contactPerson || targetDeal.contactPerson || '',
      contactDesignation: contactDesignation || targetDeal.contactDesignation || '',
      email: contactEmail || targetDeal.contactEmail || '',
      phone: contactPhone || targetDeal.contactPhone || '',
      value: val,
      notes: notes || targetDeal.notes || '',
      description: notes || targetDeal.notes || '',
      services: services?.length ? services : (targetDeal.services || []),
      owner: owner || targetDeal.owner || '',
      closeDate: closeDate || targetDeal.closeDate || '',
      stage: stage || targetDeal.stage || 'Closed Won',
    }

    const searchParams = new URLSearchParams({
      new: 'quotation',
      dealId: targetDeal.id,
      clientName: client || targetDeal.client || '',
      title: title || targetDeal.title || '',
      contactPerson: contactPerson || targetDeal.contactPerson || '',
      contactDesignation: contactDesignation || targetDeal.contactDesignation || '',
      email: contactEmail || targetDeal.contactEmail || '',
      phone: contactPhone || targetDeal.contactPhone || '',
      value: String(val),
    })

    if (addr) searchParams.set('clientAddress', addr)
    if (gst) searchParams.set('clientGstin', gst)
    const stateForQuotation = resolvePlaceOfSupply({ customerGstin: gst, customerState: clientState || targetDeal.clientState }).label
    if (stateForQuotation) searchParams.set('clientState', stateForQuotation)
    const srvs = services?.length ? services : targetDeal.services
    if (srvs && srvs.length > 0) {
      searchParams.set('services', srvs.join(','))
    }

    onClose()
    navigate(`/app/sales/quotations?${searchParams.toString()}`, {
      state: { fromDeal: payload },
    })
  }

  // Pin/Unpin deal
  const handlePinToggle = async () => {
    const targetDeal = dealData || deal
    if (!targetDeal?.id) return
    try {
      const newPinnedState = !pinned
      await api.patch(`/deals/${targetDeal.id}/pin`, { pinned: newPinnedState })
      // Update local state
      setPinned(newPinnedState)
      const updated = { ...targetDeal, pinned: newPinnedState }
      setDealData(updated)
      // Notify parent if needed
      if (onUpdate) {
        onUpdate(updated)
      }
      toast.success(newPinnedState ? 'Deal pinned' : 'Deal unpinned')
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'toggle pin status'))
    }
  }

  // The superadmin removes the deal here and now. Anyone else only raises a
  // request, which the server carries out when the superadmin approves it. The
  // quotations, invoices, project and timesheet rows the deal produced are
  // separate business records and stay in place either way.
  const handleDelete = async () => {
    if (!deal) return
    if (!isDatabaseId(deal.id)) {
      onDeleted?.(deal.id)
      setIsDeleteOpen(false)
      onClose()
      return
    }
    const reason = deleteReason.trim()
    if (!mayDeleteAlone && reason.length < 3) {
      toast.error('Say why this deal should be deleted — the superadmin reads this.')
      return
    }
    setIsDeleting(true)
    try {
      const result = await deleteRecord.mutateAsync({ entityType: 'deal', entityId: deal.id, reason })
      if (result.deleted) {
        onDeleted?.(deal.id)
        toast.success(result.message || `"${deal.title || 'This deal'}" was deleted.`)
        setIsDeleteOpen(false)
        onClose()
      } else {
        toast.success(result.message || 'Delete request sent to the superadmin for approval.')
        setIsDeleteOpen(false)
      }
      setDeleteReason('')
    } catch (err) {
      toast.error(getApiErrorMessage(err, mayDeleteAlone ? 'delete this deal' : 'send this delete request'))
    } finally {
      setIsDeleting(false)
    }
  }

  const handleWithdrawDelete = async () => {
    if (!pendingDelete) return
    try {
      await cancelDeleteRequest.mutateAsync(pendingDelete.id)
      toast.success('Delete request withdrawn. The deal stays.')
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'withdraw this delete request'))
    }
  }

  const persistChanges = async (updates: Partial<DealDetailData>) => {
    if (!deal) return
    setIsSaving(true)
    const numVal = parseFloat(String(updates.value !== undefined ? updates.value : value).replace(/[^0-9.]/g, '')) || 0
    const numProb = parseInt(String(updates.probability !== undefined ? updates.probability : probability), 10) || 50

    const updatedDeal: DealDetailData = {
      ...deal,
      id: deal.id,
      organizationId: deal.organizationId || '',
      title: updates.title !== undefined ? updates.title : title,
      client: updates.client !== undefined ? updates.client : client,
      stage: updates.stage !== undefined ? updates.stage : stage,
      value: numVal,
      probability: numProb,
      owner: updates.owner !== undefined ? updates.owner : owner,
      closeDate: updates.closeDate !== undefined ? updates.closeDate : closeDate,
      notes: updates.notes !== undefined ? updates.notes : notes,
      contactPerson: updates.contactPerson !== undefined ? updates.contactPerson : contactPerson,
      contactDesignation: updates.contactDesignation !== undefined ? updates.contactDesignation : contactDesignation,
      contactEmail: updates.contactEmail !== undefined ? updates.contactEmail : contactEmail,
      contactPhone: updates.contactPhone !== undefined ? updates.contactPhone : contactPhone,
      source: updates.source !== undefined ? updates.source : source,
      services: updates.services !== undefined ? updates.services : services,
      clientAddress: updates.clientAddress !== undefined ? updates.clientAddress : clientAddress,
      address: updates.clientAddress !== undefined ? updates.clientAddress : clientAddress,
      billingAddress: updates.clientAddress !== undefined ? updates.clientAddress : clientAddress,
      clientGstin: updates.clientGstin !== undefined ? updates.clientGstin : clientGstin,
      gstin: updates.clientGstin !== undefined ? updates.clientGstin : clientGstin,
      clientState: updates.clientState !== undefined ? updates.clientState : clientState,
    }

    // Save to the backend first — the parent list, the in-app notification
    // feed, and the "Saved" indicator below all used to update unconditionally
    // before this request even ran, so a rejected save (no permission, etc.)
    // still showed "Stage Updated" / "Saved" with no way to tell it hadn't.
    try {
      if (deal?.id && !deal.id.startsWith('deal-') && !deal.id.startsWith('d')) {
        await api.patch(`/deals/${deal.id}`, {
          title: updatedDeal.title,
          client: updatedDeal.client,
          stage: updatedDeal.stage,
          value: numVal,
          probability: numProb,
          owner: updatedDeal.owner,
          closeDate: updatedDeal.closeDate,
          notes: updatedDeal.notes,
          services: updatedDeal.services,
          contactPerson: updatedDeal.contactPerson,
          contactDesignation: updatedDeal.contactDesignation,
          contactEmail: updatedDeal.contactEmail,
          contactPhone: updatedDeal.contactPhone,
          address: updatedDeal.clientAddress,
          billingAddress: updatedDeal.clientAddress,
          clientAddress: updatedDeal.clientAddress,
          gstin: updatedDeal.clientGstin,
          clientGstin: updatedDeal.clientGstin,
          clientState: updatedDeal.clientState,
          source: updatedDeal.source,
        }, { skipErrorToast: true })
      }
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'save changes to this deal'))
      setIsSaving(false)
      return
    }

    onUpdate?.(updatedDeal)

    setSaveMessage('Saved')
    setTimeout(() => setSaveMessage(null), 2000)
    setIsSaving(false)
  }

  const handlePostComment = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!replyText.trim()) return
    try {
      await createComment(replyText.trim())
      setReplyText('')
    } catch {
      toast.error('Failed to post comment')
    }
  }

  const handleAddTodo = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!newTodo.trim()) return
    try {
      await createTodo({ text: newTodo.trim() })
      setNewTodo('')
      setIsAddingTodo(false)
    } catch {
      toast.error('Failed to add todo')
    }
  }

  const handleAddDependency = (e: React.FormEvent) => {
    e.preventDefault()
    if (!newDepTitle.trim()) return
    setDependencies((prev) => [
      ...prev,
      {
        id: `dep-${Date.now()}`,
        title: newDepTitle.trim(),
        type: 'Dependency Link',
        status: 'Active',
      },
    ])
    setNewDepTitle('')
    setIsAddingDependency(false)
  }

  const handleRequestStatusUpdate = () => {
    createComment('🔔 Requested a status update on this deal.').catch(() => {
      toast.error('Failed to request status update')
    })
    setSaveMessage('Status update requested')
    setTimeout(() => setSaveMessage(null), 2500)
  }

  // Reminder State
  const [isDealReminderModalOpen, setIsDealReminderModalOpen] = useState(false)
  const [dealReminderTitle, setDealReminderTitle] = useState('')
  const [dealReminderPreset, setDealReminderPreset] = useState<'tomorrow_morning' | 'in_2_days' | 'next_week' | 'custom'>('tomorrow_morning')
  const [dealReminderCustomDate, setDealReminderCustomDate] = useState('')
  const [dealReminderNotes, setDealReminderNotes] = useState('')
  const [isCreatingDealReminder, setIsCreatingDealReminder] = useState(false)

  const handleCreateReminder = () => {
    setDealReminderTitle(`Follow up on ${title || deal?.title || 'this deal'}`)
    setDealReminderNotes('')
    setDealReminderPreset('tomorrow_morning')
    setIsDealReminderModalOpen(true)
  }

  const handleSaveDealReminder = async () => {
    if (!dealReminderTitle.trim() || !deal?.id) return
    setIsCreatingDealReminder(true)
    try {
      let dueDate = new Date()
      if (dealReminderPreset === 'tomorrow_morning') {
        dueDate.setDate(dueDate.getDate() + 1)
        dueDate.setHours(9, 0, 0, 0)
      } else if (dealReminderPreset === 'in_2_days') {
        dueDate.setDate(dueDate.getDate() + 2)
        dueDate.setHours(10, 0, 0, 0)
      } else if (dealReminderPreset === 'next_week') {
        dueDate.setDate(dueDate.getDate() + 7)
        dueDate.setHours(9, 0, 0, 0)
      } else if (dealReminderCustomDate) {
        dueDate = new Date(dealReminderCustomDate)
      }

      await api.post(`/deals/${deal.id}/reminders`, {
        title: dealReminderTitle.trim(),
        dueDate: dueDate.toISOString(),
        notes: dealReminderNotes.trim(),
      })

      toast.success('Reminder set and added to notifications!')
      setSaveMessage('Reminder set')
      setIsDealReminderModalOpen(false)
      setTimeout(() => setSaveMessage(null), 2500)

      // Refresh deal data to show the new reminder
      if (onUpdate && deal.id) {
        const updated = await api.get(`/deals/${deal.id}`)
        if (updated) onUpdate(updated as DealDetailData)
      }
    } catch {
      toast.error('Failed to create reminder')
    } finally {
      setIsCreatingDealReminder(false)
    }
  }

  const handleMakeRecurring = () => {
    setSaveMessage('Deal recurrence: Monthly on 1st')
    setTimeout(() => setSaveMessage(null), 2500)
  }

  const handleDuplicate = () => {
    if (!deal) return
    const duplicated: DealDetailData = {
      ...deal,
      id: `deal-${Date.now()}`,
      organizationId: deal.organizationId || '',
      title: `${title} (Copy)`,
      client,
      stage,
      value: parseFloat(value) || 0,
      probability: parseInt(probability, 10) || 0,
      owner,
      closeDate,
      notes,
    }
    onUpdate?.(duplicated)
    setSaveMessage('Deal duplicated successfully!')
    setTimeout(() => setSaveMessage(null), 2500)
  }

  const handlePrioritySelect = (newPriority: string) => {
    setPriority(newPriority)
    persistChanges({ priority: newPriority } as any)
    setSaveMessage(`Priority: ${newPriority}`)
    setTimeout(() => setSaveMessage(null), 2000)
  }

  const handleAddCustomLink = (e?: React.FormEvent) => {
    if (e) e.preventDefault()
    if (!newLinkUrl.trim() || newLinkUrl === 'https://') return
    const newLink = {
      id: `link-${Date.now()}`,
      url: newLinkUrl.trim(),
      label: newLinkLabel.trim() || newLinkUrl.trim(),
    }
    setCustomLinks((prev) => [...prev, newLink])
    setNewLinkUrl('https://')
    setNewLinkLabel('')
    setIsAddingLink(false)
    setSaveMessage('Link attached')
    setTimeout(() => setSaveMessage(null), 2000)
  }

  const handleRemoveCustomLink = (id: string) => {
    setCustomLinks((prev) => prev.filter((l) => l.id !== id))
  }

  const handleAddCustomPage = () => {
    const pTitle = prompt('Enter document or page title:', 'Sales Proposal & Deliverables')
    if (pTitle && pTitle.trim()) {
      setCustomPages((prev) => [...prev, { id: `page-${Date.now()}`, title: pTitle.trim() }])
      setSaveMessage('Page attached')
      setTimeout(() => setSaveMessage(null), 2000)
    }
  }

  const handleRename = () => {
    titleInputRef.current?.focus()
    titleInputRef.current?.select()
  }

  // The won stage is not chosen by hand: a deal moves there itself once its final invoice is issued and paid.
  const STAGE_OPTIONS = dealStages.map((s) => ({
    label: s.name.toUpperCase(),
    value: s.name,
    color: s.color || '#6366f1',
    locked: Boolean(s.isWon) || s.name.toLowerCase() === 'closed won',
  }))
  const currentStageObj =
    STAGE_OPTIONS.find((s) => s.value.toLowerCase() === (stage || '').toLowerCase()) ||
    STAGE_OPTIONS[0] || { label: stage?.toUpperCase() || '', value: stage || '', color: '#6366f1' }

  // Switching stage always moves win probability with it: each pipeline stage
  // carries its own default (20% Discovery → 40% Demo → 60% Proposal → 80%
  // Negotiation), so the slider jumps +20 per forward step without a manual edit.
  const applyStageChange = (newStage: string) => {
    const stageDefault = dealStages.find((s) => s.name.toLowerCase() === newStage.toLowerCase())?.defaultProbability
    setStage(newStage)
    if (typeof stageDefault === 'number') {
      setProbability(String(stageDefault))
      persistChanges({ stage: newStage, probability: stageDefault })
    } else {
      persistChanges({ stage: newStage })
    }
  }

  if (!isOpen || !deal) return null

  const dealNumber = deal.number || (deal.id?.startsWith('deal-') ? deal.id.replace('deal-', '') : deal.id?.slice(-4) || '01')

  return (
    <>
      <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        hideCloseButton={true}
        className={`p-0 gap-0 overflow-hidden bg-background border border-border shadow-2xl transition-all duration-200 flex flex-col ${
          isFullscreen
            ? 'w-[98vw] max-w-[98vw] h-[96vh] rounded-xl'
            : 'w-[95vw] max-w-[1240px] xl:max-w-[1340px] h-[90vh] max-h-[920px] rounded-2xl'
        }`}
      >
        {/* Top Header Bar */}
        <div className="flex items-center justify-between px-6 py-3.5 border-b border-border bg-muted/20 select-none shrink-0">
          <div className="flex items-center gap-3 text-xs text-muted-foreground">
            <span className="font-mono font-semibold px-2 py-0.5 rounded bg-muted text-foreground/80">
              #{dealNumber}
            </span>
            <div className="flex items-center gap-1.5">
              <Link to="/app/deals" className="hover:text-foreground transition-colors">
                Sales
              </Link>
              <span>›</span>
              <Link to="/app/deals" className="hover:text-foreground transition-colors">
                Deals
              </Link>
              <span>›</span>
              <Link to={currentSectionHref || '/app/deals'} className="hover:text-foreground transition-colors font-medium text-foreground">
                {currentSectionName}
              </Link>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {saveMessage && (
              <span className="text-xs text-emerald-600 font-medium px-2 py-0.5 bg-emerald-500/10 rounded animate-in fade-in">
                {saveMessage}
              </span>
            )}
            <button
              type="button"
              title={pinned ? "Unpin Item" : "Pin Item"}
              onClick={handlePinToggle}
              className={`p-1.5 rounded-lg transition-colors ${
                pinned
                  ? 'text-amber-500 hover:bg-amber-500/10'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted/60'
              }`}
            >
              <Pin className={`h-4 w-4 ${pinned ? 'fill-current' : ''}`} />
            </button>
            <button
              onClick={() => setIsFullscreen(!isFullscreen)}
              title={isFullscreen ? 'Restore' : 'Fullscreen'}
              className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/60"
            >
              {isFullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
            </button>
            <button onClick={onClose} title="Close" className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/60 ml-1">
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Main 2-Column Body */}
        <div className="flex-1 flex flex-col md:flex-row overflow-hidden min-h-0">
          {/* Left Column */}
          <div className="flex-1 min-w-0 flex flex-col border-r border-border overflow-y-auto p-6 space-y-6">
            <div className="flex items-start justify-between gap-4">
              <div className="flex flex-col gap-2 flex-1">
                <div className="flex items-center gap-3">
                  <span className="p-2 rounded-xl bg-emerald-500/10 text-emerald-600">
                    <BadgeDollarSign className="h-5 w-5" />
                  </span>
                  <input
                    ref={titleInputRef}
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    onBlur={() => persistChanges({ title })}
                    className="text-xl font-bold text-foreground bg-transparent border-b border-transparent hover:border-border focus:border-primary focus:outline-none w-full py-0.5"
                    placeholder="Deal Title"
                  />
                </div>
                
                {services && services.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 ml-[52px]">
                    {services.map((s) => (
                      <Badge key={s} variant="secondary" className="text-[10px] font-semibold text-purple-700 bg-purple-500/10 dark:text-purple-300">
                        {s}
                      </Badge>
                    ))}
                  </div>
                )}
              </div>

              {/* Action Buttons Row with Create Quotation, Create Space & + Add Dropdown */}
              <div className="flex items-center gap-1.5 text-muted-foreground shrink-0 flex-wrap">
                {deal?.quotation ? (
                  <DealQuotationLink quotation={deal.quotation} className="h-7 px-3" />
                ) : (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleCreateQuotation(deal)}
                    className="h-7 text-xs gap-1.5 border-indigo-200 dark:border-indigo-800 text-indigo-700 dark:text-indigo-300 hover:bg-indigo-50 dark:hover:bg-indigo-950/40 shadow-2xs font-semibold"
                  >
                    <FileText className="h-3.5 w-3.5 text-indigo-600 dark:text-indigo-400" />
                    <span>Create Quotation</span>
                  </Button>
                )}

                {linkedProject ? (
                  canViewSpace && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      onClose()
                      navigate(`/spaces/${linkedProject.id}`)
                    }}
                    className="h-7 text-xs gap-1.5 border-emerald-500/40 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-50 dark:hover:bg-emerald-950/40 shadow-2xs font-semibold"
                  >
                    <ExternalLink className="h-3.5 w-3.5 text-emerald-600" />
                    <span>View Space</span>
                    <span className="text-[10px] font-mono px-1 py-0.2 rounded bg-emerald-100/80 dark:bg-emerald-900/60 text-emerald-800 dark:text-emerald-200">
                      {linkedProject.key}
                    </span>
                  </Button>
                  )
                ) : (
                  canCreateSpace && (
                  <Button
                    size="sm"
                    onClick={handleCreateSpaceProject}
                    disabled={isCreatingProject}
                    className="h-7 text-xs gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white shadow-2xs font-semibold"
                  >
                    {isCreatingProject ? (
                      <>
                        <RefreshCw className="h-3 w-3 animate-spin" />
                        <span>Creating Space...</span>
                      </>
                    ) : (
                      <>
                        <Rocket className="h-3 w-3" />
                        <span>Create Space</span>
                      </>
                    )}
                  </Button>
                  )
                )}

                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button size="sm" variant="outline" className="h-7 text-xs gap-1 shadow-xs hover:bg-accent hover:text-accent-foreground">
                      <Plus className="h-3.5 w-3.5" /> Add
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-64">
                    <DropdownMenuLabel className="text-xs text-muted-foreground font-semibold">
                      Add Associated Items
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator />

                    {/* 0. Create Quotation */}
                    <DropdownMenuItem
                      onClick={() => (deal?.quotation ? navigate(quotationViewPath(deal.quotation)) : handleCreateQuotation(deal))}
                      className="cursor-pointer flex items-center justify-between text-xs py-2 bg-indigo-50/60 dark:bg-indigo-950/30 mb-1"
                    >
                      <div className="flex items-center gap-2">
                        <FileText className="h-4 w-4 text-indigo-600" />
                        <div>
                          <p className="font-semibold text-foreground">{deal?.quotation ? 'View Quotation' : 'Create Quotation'}</p>
                          <p className="text-[10px] text-muted-foreground">{deal?.quotation ? deal.quotation.number : 'Generate proposal from this deal'}</p>
                        </div>
                      </div>
                      <Link
                        to="/app/sales/quotations"
                        target="_blank"
                        onClick={(e: React.MouseEvent) => e.stopPropagation()}
                        className="text-muted-foreground hover:text-primary p-1"
                        title="Open /app/sales/quotations"
                      >
                        <ExternalLink className="h-3 w-3" />
                      </Link>
                    </DropdownMenuItem>

                    {/* 1. TODO list */}
                    <DropdownMenuItem
                      onClick={() => setIsAddingTodo(true)}
                      className="cursor-pointer flex items-center justify-between text-xs py-2"
                    >
                      <div className="flex items-center gap-2">
                        <ListTodo className="h-4 w-4 text-emerald-600" />
                        <div>
                          <p className="font-semibold text-foreground">TODO list</p>
                          <p className="text-[10px] text-muted-foreground">Deal checklist & milestones</p>
                        </div>
                      </div>
                    </DropdownMenuItem>


                    {/* 5. Space / Project (/spaces) */}
                    {(linkedProject ? canViewSpace : canCreateSpace) && (
                    <DropdownMenuItem
                      onClick={() => {
                        if (linkedProject) {
                          onClose()
                          navigate(`/spaces/${linkedProject.id}`)
                        } else {
                          handleCreateSpaceProject()
                        }
                      }}
                      className="cursor-pointer flex items-center justify-between text-xs py-2 bg-emerald-50/60 dark:bg-emerald-950/30"
                    >
                      <div className="flex items-center gap-2">
                        {linkedProject ? (
                          <ExternalLink className="h-4 w-4 text-emerald-600" />
                        ) : (
                          <Rocket className="h-4 w-4 text-emerald-600" />
                        )}
                        <div>
                          <p className="font-semibold text-foreground">
                            {linkedProject ? 'View Space' : 'Create Space / Project'}
                          </p>
                          <p className="text-[10px] text-muted-foreground">
                            {linkedProject ? `Direct to linked space [${linkedProject.key}]` : 'Deploy to /spaces'}
                          </p>
                        </div>
                      </div>
                      {linkedProject ? (
                        <span className="text-[10px] font-mono font-bold text-emerald-700 dark:text-emerald-400">
                          {linkedProject.key} →
                        </span>
                      ) : (
                        <Rocket className="h-3 w-3 text-muted-foreground" />
                      )}
                    </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>



                {/* Lightning Actions Dropdown (Zap) */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      className="p-1.5 rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                      title="Quick actions & automations"
                    >
                      <Zap className="h-4 w-4 text-amber-500" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    {/* Update Status */}
                    <DropdownMenuItem
                      onClick={() => {
                        setSaveMessage('Change deal stage on sidebar')
                        setTimeout(() => setSaveMessage(null), 2500)
                      }}
                      className="cursor-pointer flex items-center gap-2.5 text-xs py-2 text-foreground font-medium"
                    >
                      <Activity className="h-4 w-4 text-blue-500" />
                      <span>Update Status</span>
                    </DropdownMenuItem>

                    {/* Request status update */}
                    <DropdownMenuItem
                      onClick={handleRequestStatusUpdate}
                      className="cursor-pointer flex items-center gap-2.5 text-xs py-2 text-foreground font-medium"
                    >
                      <HeartPulse className="h-4 w-4 text-muted-foreground" />
                      <span>Request status update</span>
                    </DropdownMenuItem>

                    <DropdownMenuSeparator />

                    {/* Create Reminder */}
                    <DropdownMenuItem
                      onClick={handleCreateReminder}
                      className="cursor-pointer flex items-center gap-2.5 text-xs py-2 text-foreground font-medium"
                    >
                      <Bell className="h-4 w-4 text-muted-foreground" />
                      <span>Create Reminder</span>
                    </DropdownMenuItem>

                    <DropdownMenuSeparator />

                    {/* Make recurring */}
                    <DropdownMenuItem
                      onClick={handleMakeRecurring}
                      className="cursor-pointer flex items-center gap-2.5 text-xs py-2 text-foreground font-medium"
                    >
                      <Repeat className="h-4 w-4 text-muted-foreground" />
                      <span>Make recurring</span>
                    </DropdownMenuItem>


                  </DropdownMenuContent>
                </DropdownMenu>

                {/* Settings Dropdown (Form & Layout Preferences) */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      className="p-1.5 rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors relative"
                      title="Form & Layout Settings"
                    >
                      <Settings className="h-4 w-4" />
                      {watchStatus === 'muted' && (
                        <span className="absolute top-0.5 right-0.5 h-1.5 w-1.5 rounded-full bg-amber-500" />
                      )}
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-72 p-1.5 bg-popover border border-border shadow-2xl rounded-2xl space-y-1">
                    <div className="px-2.5 py-1.5 border-b border-border/60">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-bold text-foreground flex items-center gap-1.5">
                          <Settings className="h-3.5 w-3.5 text-primary" /> Deal Form Settings
                        </span>
                      </div>
                      <p className="text-[10px] text-muted-foreground mt-0.5">
                        Customize visible sections and preferences
                      </p>
                    </div>

                    {/* Notification & Watch Status */}
                    <DropdownMenuLabel className="text-[10px] uppercase font-bold text-muted-foreground px-2 pt-1 pb-0.5">
                      Notifications & Watch
                    </DropdownMenuLabel>
                    <div className="grid grid-cols-3 gap-1 px-1 pb-1">
                      <button
                        type="button"
                        onClick={() => {
                          setWatchStatus('watching')
                          toast.success(`You are now watching all updates for deal "${title}"`)
                        }}
                        className={`text-[10px] py-1 px-1.5 rounded-md font-semibold text-center transition-all ${
                          watchStatus === 'watching'
                            ? 'bg-blue-600 text-white shadow-xs'
                            : 'bg-muted/60 text-muted-foreground hover:text-foreground'
                        }`}
                      >
                        Watching
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setWatchStatus('normal')
                          toast.success(`Notification mode set to Mentions Only for "${title}"`)
                        }}
                        className={`text-[10px] py-1 px-1.5 rounded-md font-semibold text-center transition-all ${
                          watchStatus === 'normal'
                            ? 'bg-blue-600 text-white shadow-xs'
                            : 'bg-muted/60 text-muted-foreground hover:text-foreground'
                        }`}
                      >
                        Normal
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setWatchStatus('muted')
                          toast.success(`Notifications muted for deal "${title}"`)
                        }}
                        className={`text-[10px] py-1 px-1.5 rounded-md font-semibold text-center transition-all ${
                          watchStatus === 'muted'
                            ? 'bg-rose-600 text-white shadow-xs'
                            : 'bg-muted/60 text-muted-foreground hover:text-foreground'
                        }`}
                      >
                        Muted
                      </button>
                    </div>

                    <DropdownMenuSeparator />

                    {/* Section Visibility Toggles */}
                    <DropdownMenuLabel className="text-[10px] uppercase font-bold text-muted-foreground px-2 pt-1 pb-0.5">
                      Visible Form Sections
                    </DropdownMenuLabel>

                    <div className="space-y-0.5 px-1">
                      <div
                        onClick={() =>
                          setVisibleSections((prev) => ({ ...prev, todos: !prev.todos }))
                        }
                        className="flex items-center justify-between px-2 py-1.5 rounded-lg hover:bg-muted/70 text-xs cursor-pointer font-medium"
                      >
                        <span className="flex items-center gap-2 text-foreground">
                          <ListTodo className="h-3.5 w-3.5 text-emerald-600" />
                          Milestones (TODO List)
                        </span>
                        {visibleSections.todos ? (
                          <Eye className="h-3.5 w-3.5 text-primary" />
                        ) : (
                          <EyeOff className="h-3.5 w-3.5 text-muted-foreground opacity-60" />
                        )}
                      </div>

                      <div
                        onClick={() =>
                          setVisibleSections((prev) => ({
                            ...prev,
                            dependencies: !prev.dependencies,
                          }))
                        }
                        className="flex items-center justify-between px-2 py-1.5 rounded-lg hover:bg-muted/70 text-xs cursor-pointer font-medium"
                      >
                        <span className="flex items-center gap-2 text-foreground">
                          <GitMerge className="h-3.5 w-3.5 text-indigo-600" />
                          Dependencies (Waterfall)
                        </span>
                        {visibleSections.dependencies ? (
                          <Eye className="h-3.5 w-3.5 text-primary" />
                        ) : (
                          <EyeOff className="h-3.5 w-3.5 text-muted-foreground opacity-60" />
                        )}
                      </div>

                      <div
                        onClick={() =>
                          setVisibleSections((prev) => ({
                            ...prev,
                            customFields: !prev.customFields,
                          }))
                        }
                        className="flex items-center justify-between px-2 py-1.5 rounded-lg hover:bg-muted/70 text-xs cursor-pointer font-medium"
                      >
                        <span className="flex items-center gap-2 text-foreground">
                          <LinkIcon className="h-3.5 w-3.5 text-purple-600" />
                          Custom Fields & Links
                        </span>
                        {visibleSections.customFields ? (
                          <Eye className="h-3.5 w-3.5 text-primary" />
                        ) : (
                          <EyeOff className="h-3.5 w-3.5 text-muted-foreground opacity-60" />
                        )}
                      </div>

                      <div
                        onClick={() =>
                          setVisibleSections((prev) => ({ ...prev, probability: !prev.probability }))
                        }
                        className="flex items-center justify-between px-2 py-1.5 rounded-lg hover:bg-muted/70 text-xs cursor-pointer font-medium"
                      >
                        <span className="flex items-center gap-2 text-foreground">
                          <Percent className="h-3.5 w-3.5 text-emerald-500" />
                          Win Probability Slider
                        </span>
                        {visibleSections.probability ? (
                          <Eye className="h-3.5 w-3.5 text-primary" />
                        ) : (
                          <EyeOff className="h-3.5 w-3.5 text-muted-foreground opacity-60" />
                        )}
                      </div>

                      <div
                        onClick={() =>
                          setVisibleSections((prev) => ({ ...prev, crm: !prev.crm }))
                        }
                        className="flex items-center justify-between px-2 py-1.5 rounded-lg hover:bg-muted/70 text-xs cursor-pointer font-medium"
                      >
                        <span className="flex items-center gap-2 text-foreground">
                          <Building2 className="h-3.5 w-3.5 text-slate-500" />
                          CRM Account & Contacts
                        </span>
                        {visibleSections.crm ? (
                          <Eye className="h-3.5 w-3.5 text-primary" />
                        ) : (
                          <EyeOff className="h-3.5 w-3.5 text-muted-foreground opacity-60" />
                        )}
                      </div>
                    </div>

                    <DropdownMenuSeparator />

                    {/* Comment Sorting */}
                    <div className="px-2 py-1 flex items-center justify-between text-xs">
                      <span className="text-muted-foreground flex items-center gap-1.5 font-medium">
                        <ArrowDownUp className="h-3.5 w-3.5" /> Activity Order
                      </span>
                      <button
                        type="button"
                        onClick={() =>
                          setCommentSortOrder((prev) =>
                            prev === 'oldest' ? 'newest' : 'oldest'
                          )
                        }
                        className="text-[11px] font-bold text-primary hover:underline"
                      >
                        {commentSortOrder === 'oldest' ? 'Oldest first' : 'Newest first'}
                      </button>
                    </div>

                    <DropdownMenuSeparator />

                    <div className="pt-1 pb-0.5 px-2 flex justify-between items-center text-[10px] text-muted-foreground">
                      <span>Preferences saved automatically</span>
                      <button
                        type="button"
                        onClick={() => {
                          setVisibleSections({
                            probability: true,
                            todos: true,
                            dependencies: true,
                            customFields: true,
                            crm: true,
                            systemLogs: true,
                          })
                          setCommentSortOrder('oldest')
                          setWatchStatus('watching')
                          setSaveMessage('Settings reset to default')
                          setTimeout(() => setSaveMessage(null), 2000)
                        }}
                        className="text-primary hover:underline font-semibold"
                      >
                        Reset All
                      </button>
                    </div>
                  </DropdownMenuContent>
                </DropdownMenu>

                {/* More Options Dropdown (3 Dots) */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      className="p-1.5 rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                      title="More options"
                    >
                      <MoreHorizontal className="h-4 w-4" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48">
                    {/* Rename */}
                    <DropdownMenuItem
                      onClick={handleRename}
                      className="cursor-pointer flex items-center gap-2.5 text-xs py-2 text-foreground font-medium"
                    >
                      <Pencil className="h-4 w-4 text-muted-foreground" />
                      <span>Rename</span>
                    </DropdownMenuItem>

                    <DropdownMenuSeparator />

                    {/* Archive Deal */}
                    <DropdownMenuItem
                      onClick={() => {
                        persistChanges({ stage: 'Closed Lost' })
                        setSaveMessage('Archived to Closed Lost')
                        setTimeout(() => setSaveMessage(null), 2500)
                      }}
                      className="cursor-pointer flex items-center gap-2.5 text-xs py-2 text-muted-foreground hover:text-foreground"
                    >
                      <Archive className="h-4 w-4" />
                      <span>Archive deal</span>
                    </DropdownMenuItem>

                    {/* Delete deal: the superadmin deletes, sales asks first. */}
                    {pendingDelete ? (
                      <>
                        <DropdownMenuItem
                          className="cursor-default flex items-center gap-2.5 text-xs py-2 text-amber-600 dark:text-amber-400"
                        >
                          <Clock className="h-4 w-4" />
                          <span>Delete requested — awaiting approval</span>
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={handleWithdrawDelete}
                          className="cursor-pointer flex items-center gap-2.5 text-xs py-2 text-muted-foreground hover:text-foreground"
                        >
                          <Ban className="h-4 w-4" />
                          <span>Withdraw delete request</span>
                        </DropdownMenuItem>
                      </>
                    ) : (
                      <DropdownMenuItem
                        onClick={() => {
                          setDeleteReason('')
                          setIsDeleteOpen(true)
                        }}
                        className="cursor-pointer flex items-center gap-2.5 text-xs py-2 text-destructive hover:bg-destructive/10"
                      >
                        <Trash2 className="h-4 w-4" />
                        <span>{mayDeleteAlone ? 'Delete deal' : 'Request deal deletion'}</span>
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>

            {/* Interactive Deal Stage Progression Stepper Bar */}
            <div className="p-3 bg-muted/20 border border-border/60 rounded-xl space-y-2.5 shadow-xs">
              <div className="flex items-center justify-between text-xs">
                <span className="font-semibold text-muted-foreground flex items-center gap-1.5">
                  <Kanban className="h-4 w-4 text-primary" /> Deal Stage Switcher
                </span>
                <span
                  className="text-[10px] font-extrabold px-2.5 py-0.5 rounded-full uppercase tracking-wider text-white"
                  style={{ backgroundColor: currentStageObj.color }}
                >
                  {currentStageObj.label}
                </span>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-1.5 pt-0.5">
                {STAGE_OPTIONS.map((opt) => {
                  const isCurrent = opt.value.toLowerCase() === (stage || '').toLowerCase()
                  const lockedWon = opt.locked && !isCurrent
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      disabled={lockedWon}
                      title={lockedWon ? 'Becomes Closed Won automatically once the final tax invoice is issued and every payment is received' : undefined}
                      onClick={() => {
                        if (lockedWon) return
                        applyStageChange(opt.value)
                      }}
                      className={`flex items-center justify-center gap-1.5 px-2 py-2 rounded-lg text-xs font-bold transition-all text-center ${lockedWon ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'} ${
                        isCurrent
                          ? 'text-white shadow-md ring-2 ring-primary/40 scale-[1.02]'
                          : 'bg-card border border-border/70 text-muted-foreground hover:text-foreground hover:bg-muted/80'
                      }`}
                      style={isCurrent ? { backgroundColor: opt.color } : undefined}
                    >
                      {isCurrent && <Check className="h-3.5 w-3.5 shrink-0" />}
                      <span className="truncate">{opt.label}</span>
                    </button>
                  )
                })}
              </div>
            </div>

            {/* Won Deal Banner & Project Creation Callout */}
            {canLaunchProject && (
              <div className="p-4 rounded-xl bg-gradient-to-r from-emerald-500/15 via-emerald-500/10 to-teal-500/15 border border-emerald-500/30 space-y-3 shadow-xs animate-in fade-in slide-in-from-top-2">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <div className="p-2.5 rounded-xl bg-emerald-600 text-white shadow-sm shrink-0">
                      <Rocket className="h-5 w-5" />
                    </div>
                    <div>
                      <h4 className="text-sm font-bold text-foreground flex items-center gap-2">
                        <span>{isWonStage ? 'Deal Won! 🎉' : 'Advance received — ready to start'}</span>
                        {linkedProject && canViewSpace && (
                          <Badge className="bg-emerald-600 text-white text-[10px] font-mono hover:bg-emerald-600">
                            Space: {linkedProject.key}
                          </Badge>
                        )}
                      </h4>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {linkedProject
                          ? `A dedicated delivery space has been created for this won deal and is live at /spaces.`
                          : canCreateSpace
                            ? (isWonStage
                              ? `This sales opportunity is Closed Won. Create an active delivery space to track execution at /spaces.`
                              : `The advance payment is in. Create a delivery space to start work; the deal closes as won once the final invoice is paid.`)
                            : `This sales opportunity is Closed Won.`}
                      </p>
                    </div>
                  </div>
                  <div className="shrink-0 flex items-center gap-2 flex-wrap">
                    {deal?.quotation ? (
                      <DealQuotationLink quotation={deal.quotation} className="h-8 px-3 bg-white/80 dark:bg-slate-900/80" />
                    ) : (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleCreateQuotation(deal)}
                        className="bg-white/80 hover:bg-white dark:bg-slate-900/80 dark:hover:bg-slate-900 border-emerald-500/40 text-emerald-700 dark:text-emerald-300 text-xs font-semibold shadow-2xs gap-1.5 h-8"
                      >
                        <FileText className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400" />
                        <span>Create Quotation</span>
                      </Button>
                    )}
                    {linkedProject ? (
                      canViewSpace && (
                      <button
                        type="button"
                        onClick={() => {
                          onClose()
                          navigate(`/spaces/${linkedProject.id}`)
                        }}
                        className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold shadow-sm transition-all hover:scale-[1.02] cursor-pointer"
                      >
                        <ExternalLink className="h-3.5 w-3.5" />
                        <span>View Space</span>
                      </button>
                      )
                    ) : (
                      canCreateSpace && (
                      <Button
                        size="sm"
                        onClick={handleCreateSpaceProject}
                        disabled={isCreatingProject}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold shadow-sm gap-1.5"
                      >
                        {isCreatingProject ? (
                          <>
                            <RefreshCw className="h-3.5 w-3.5 animate-spin" />
                            <span>Creating Space...</span>
                          </>
                        ) : (
                          <>
                            <Rocket className="h-3.5 w-3.5" />
                            <span>Create Space / Project</span>
                          </>
                        )}
                      </Button>
                      )
                    )}
                  </div>
                </div>

                {projectBanner && (
                  <div className="p-2.5 rounded-lg bg-emerald-100/80 dark:bg-emerald-950/70 border border-emerald-300 dark:border-emerald-800 text-emerald-800 dark:text-emerald-200 text-xs flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4 text-emerald-600 dark:text-emerald-400 shrink-0" />
                      <span>{projectBanner}</span>
                    </div>
                    {linkedProject && canViewSpace && (
                      <button
                        type="button"
                        onClick={() => {
                          onClose()
                          navigate(`/spaces/${linkedProject.id}`)
                        }}
                        className="font-bold underline hover:no-underline ml-2 shrink-0 cursor-pointer"
                      >
                        View Space →
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Notes / Description */}
            <div className="space-y-1">
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                onBlur={() => persistChanges({ notes })}
                placeholder="Add deal description, scope of work or notes..."
                rows={3}
                className="w-full text-sm text-foreground bg-transparent border border-transparent hover:border-border/60 focus:border-border rounded-lg p-2 focus:outline-none transition-colors resize-none placeholder:text-muted-foreground/60"
              />
            </div>

            {/* 1. Milestones & Checklist (TODO List) */}
            {visibleSections.todos && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-bold text-foreground flex items-center gap-2">
                    <ListTodo className="h-4 w-4 text-emerald-600" /> Deal Milestones & Tasks (TODO)
                  </h3>
                  <span className="text-xs text-muted-foreground">
                    {todos.filter((t) => t.done).length}/{todos.length} done
                  </span>
                </div>

                <div className="space-y-1.5">
                  {todos.map((todo) => (
                    <div
                      key={todo.id}
                      className="flex items-center gap-2.5 text-sm p-1.5 rounded-lg hover:bg-muted/40 transition-colors group"
                    >
                      <button
                        type="button"
                        onClick={() => toggleTodo(todo.id, !todo.done)}
                        className="shrink-0"
                      >
                        {todo.done ? (
                          <CheckSquare className="h-4 w-4 text-emerald-500" />
                        ) : (
                          <Square className="h-4 w-4 text-muted-foreground group-hover:text-foreground" />
                        )}
                      </button>
                      <span className={`flex-1 ${todo.done ? 'line-through text-muted-foreground' : 'text-foreground'}`}>
                        {todo.text}
                      </span>
                      <button
                        type="button"
                        onClick={() => deleteTodo(todo.id)}
                        className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive transition-opacity shrink-0"
                        title="Delete todo"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
                </div>

                {isAddingTodo ? (
                  <form onSubmit={handleAddTodo} className="flex items-center gap-2 pt-1">
                    <Input
                      autoFocus
                      value={newTodo}
                      onChange={(e) => setNewTodo(e.target.value)}
                      placeholder="Enter new milestone or todo..."
                      className="h-8 text-xs"
                    />
                    <Button type="submit" size="sm" className="h-8 text-xs">
                      Add
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-8 text-xs"
                      onClick={() => setIsAddingTodo(false)}
                    >
                      Cancel
                    </Button>
                  </form>
                ) : (
                  <button
                    onClick={() => setIsAddingTodo(true)}
                    className="flex items-center gap-1.5 text-xs text-primary font-medium hover:underline pt-1"
                  >
                    <Plus className="h-3.5 w-3.5" /> Add milestone / TODO
                  </button>
                )}
              </div>
            )}


            {/* Messages / Activity Feed */}
            <div className="space-y-4 pt-4 border-t border-border/60">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-bold text-foreground">Deal Activity & Timeline</h3>
                <span className="text-[11px] text-muted-foreground">
                  Sorted: {commentSortOrder === 'oldest' ? 'Oldest first' : 'Newest first'}
                </span>
              </div>

              <div className="space-y-3">
                {[...comments]
                  .sort((a, b) =>
                    commentSortOrder === 'newest'
                      ? b.id.localeCompare(a.id)
                      : a.id.localeCompare(b.id)
                  )
                  .map((item) => (
                  <div key={item.id} className="flex items-start gap-3 text-xs">
                    {item.avatarUrl ? (
                      <img
                        src={item.avatarUrl}
                        alt={item.author}
                        className="h-6 w-6 rounded-full object-cover shrink-0 mt-0.5"
                      />
                    ) : (
                      <div className="h-6 w-6 rounded-full bg-primary text-white flex items-center justify-center font-bold text-[10px] shrink-0 mt-0.5">
                        {item.author.charAt(0).toUpperCase()}
                      </div>
                    )}
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <span className="font-semibold text-foreground">{item.author}</span>
                        <span className="text-muted-foreground text-[11px]">
                          {item.createdAt ? formatDistanceToNow(new Date(item.createdAt), { addSuffix: true }) : ''}
                        </span>
                      </div>
                      <p className="text-muted-foreground mt-0.5">{item.text}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Reply / Comment Box */}
              <form onSubmit={handlePostComment} className="mt-4 pt-2">
                <div className="border border-border rounded-xl bg-card overflow-hidden focus-within:border-primary/60 transition-colors">
                  <textarea
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    placeholder="Add an update or note..."
                    rows={3}
                    className="w-full text-xs text-foreground bg-transparent p-3 focus:outline-none resize-none placeholder:text-muted-foreground"
                  />
                  <div className="flex items-center justify-between px-3 py-2 bg-muted/20 border-t border-border/40">
                    <span className="text-[10px] text-muted-foreground">Press Enter to post</span>
                    <Button type="submit" size="sm" className="h-7 text-xs gap-1">
                      <Send className="h-3 w-3" /> Post
                    </Button>
                  </div>
                </div>
              </form>
            </div>
          </div>

          {/* Right Column: Deal Sidebar */}
          <div className="w-full md:w-[380px] lg:w-[420px] shrink-0 bg-muted/10 p-6 space-y-6 overflow-y-auto border-l border-border/40">
            {/* Stage Dropdown */}
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2 text-xs font-semibold text-muted-foreground shrink-0">
                <CheckCircle2 className="h-4 w-4" /> Stage
              </div>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    className="px-3 py-1.5 rounded-md text-xs font-bold uppercase tracking-wider cursor-pointer border-none shadow-sm flex items-center gap-1.5 text-white"
                    style={{ backgroundColor: currentStageObj.color }}
                  >
                    <span>{currentStageObj.label}</span>
                    <ChevronDown className="h-3 w-3 opacity-70" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56 p-1 bg-popover border border-border shadow-2xl rounded-xl">
                  <DropdownMenuLabel className="text-[10px] uppercase font-bold text-muted-foreground px-2 py-1">
                    Select Deal Stage
                  </DropdownMenuLabel>
                  {STAGE_OPTIONS.map((opt) => {
                    const isSelected = opt.value.toLowerCase() === (stage || '').toLowerCase()
                    return (
                      <DropdownMenuItem
                        key={opt.value}
                        onClick={() => applyStageChange(opt.value)}
                        className={`cursor-pointer flex items-center justify-between text-xs py-2 px-2.5 rounded-md font-medium transition-colors ${
                          isSelected ? 'bg-muted font-bold' : ''
                        }`}
                      >
                        <span className="flex items-center gap-2 text-foreground">
                          <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: opt.color }} />
                          {opt.label}
                        </span>
                        {isSelected && <Check className="h-3.5 w-3.5 text-primary shrink-0" />}
                      </DropdownMenuItem>
                    )
                  })}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            {/* Probability Slider */}
            {visibleSections.probability && (
              <div className="space-y-2 text-xs">
                <div className="flex items-center justify-between">
                  <span className="flex items-center gap-2 text-muted-foreground font-medium">
                    <Percent className="h-4 w-4 text-emerald-500" /> Win Probability
                  </span>
                  <span className="font-bold text-emerald-600 dark:text-emerald-400 text-sm">{probability}%</span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="100"
                  step="5"
                  value={probability}
                  onChange={(e) => setProbability(e.target.value)}
                  onMouseUp={() => persistChanges({ probability: parseInt(probability, 10) || 50 })}
                  onTouchEnd={() => persistChanges({ probability: parseInt(probability, 10) || 50 })}
                  className="w-full accent-emerald-500 cursor-pointer h-2 bg-muted rounded-lg"
                />
              </div>
            )}

            {/* Deal Value */}
            <div className="flex items-center justify-between text-xs">
              <span className="flex items-center gap-2 text-muted-foreground font-medium">
                <TrendingUp className="h-4 w-4 text-emerald-500" /> Deal Value
              </span>
              <div className="flex items-center gap-1 font-bold text-base text-emerald-600 dark:text-emerald-400">
                <span>₹</span>
                <input
                  type="text"
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  onBlur={() => persistChanges({ value: parseFloat(value.replace(/[^0-9.]/g, '')) || 0 })}
                  className="w-28 text-right bg-transparent border-b border-transparent hover:border-border focus:border-primary focus:outline-none py-0.5"
                  placeholder="50,000"
                />
              </div>
            </div>

            {/* Close Date */}
            <div className="flex items-center justify-between text-xs">
              <span className="flex items-center gap-2 text-muted-foreground font-medium">
                <Calendar className="h-4 w-4" /> Target Close
              </span>
              <CalendarPickerDropdown
                value={closeDate}
                onChange={(newDate) => {
                  setCloseDate(newDate)
                  persistChanges({ closeDate: newDate })
                }}
                trigger={
                  <button
                    type="button"
                    className="text-right text-muted-foreground hover:text-foreground font-medium hover:underline cursor-pointer flex items-center gap-1.5"
                    title="Click to pick target close date from calendar"
                  >
                    <span>{closeDate || 'Not scheduled'}</span>
                    <Calendar className="h-3 w-3 opacity-60" />
                  </button>
                }
              />
            </div>

            {/* Deal Owner */}
            <div className="flex items-center justify-between text-xs">
              <span className="flex items-center gap-2 text-muted-foreground font-medium">
                <User className="h-4 w-4" /> Deal Owner
              </span>
              <select
                value={owner}
                onChange={(e) => {
                  setOwner(e.target.value)
                  persistChanges({ owner: e.target.value })
                }}
                className="bg-transparent font-semibold text-foreground focus:outline-none cursor-pointer text-right"
              >
                {teamMembers.map((m) => (
                  <option key={m.name} value={m.name} className="bg-popover text-popover-foreground">
                    {m.name}
                  </option>
                ))}
                {owner && !teamMembers.some((m) => m.name === owner) && (
                  <option value={owner} className="bg-popover text-popover-foreground">
                    {owner}
                  </option>
                )}
              </select>
            </div>

            {/* Services Section */}
            <div className="pt-4 border-t border-border/60 space-y-3">
              {/* Total from Master Data Banner */}
              {services && services.length > 0 && (
                <div className="flex items-center justify-between p-2.5 rounded-lg bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800">
                  <span className="text-[11px] font-semibold text-emerald-700 dark:text-emerald-300">
                    Total (from Master Data)
                  </span>
                  <span className="text-sm font-bold text-emerald-800 dark:text-emerald-200">
                    {formatCurrency(totalFromServices)}
                  </span>
                </div>
              )}
              <ServicesPicker
                selectedServices={services}
                onChange={(newServices) => {
                  setServices(newServices)
                  persistChanges({ services: newServices })
                }}
                label="Services"
                description="Services associated with this deal. Rates and totals are fetched from master data."
                showRates={true}
                masterData={masterServices}
              />
            </div>

{/* Priority Display Field */}
              <div className="flex items-center gap-2 pt-2">
                <Flag className="h-4 w-4 text-rose-500 shrink-0" />
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="flex items-center gap-1.5 w-full py-1 text-xs font-medium bg-transparent border-b border-transparent hover:border-border focus:border-primary focus:outline-none cursor-pointer">
                      {(() => {
                        const currentOpt = PRIORITY_OPTIONS.find((p) => p.value === priority) || PRIORITY_OPTIONS[4]
                        return (
                          <>
                            <span className={currentOpt.color}>{currentOpt.label}</span>
                          </>
                        )
                      })()}
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="w-44 p-1.5">
                    {PRIORITY_OPTIONS.map((opt) => {
                      const IconComponent = opt.icon
                      const isSelected = opt.value === priority
                      return (
                        <DropdownMenuItem
                          key={opt.value}
                          onClick={() => handlePrioritySelect(opt.value)}
                          className={`flex items-center gap-2.5 px-2 py-1.5 text-xs rounded-md cursor-pointer font-medium ${
                            isSelected ? 'bg-muted font-bold' : ''
                          }`}
                        >
                          <IconComponent className={`h-4 w-4 ${opt.color} shrink-0`} />
                          <span className="text-foreground">{opt.label}</span>
                        </DropdownMenuItem>
                      )
                    })}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>

              {/* Custom Links Attached */}
              {customLinks.length > 0 && (
                <div className="space-y-1.5 pt-1">
                  <span className="text-[11px] font-semibold text-muted-foreground flex items-center gap-1.5">
                    <LinkIcon className="h-3 w-3 text-blue-500" /> Links & Attachments
                  </span>
                  <div className="space-y-1">
                    {customLinks.map((lnk) => (
                      <div
                        key={lnk.id}
                        className="flex items-center justify-between p-1.5 rounded-lg border border-border/60 bg-muted/20 text-xs hover:border-border transition-colors group"
                      >
                        <a
                          href={lnk.url.startsWith('http') ? lnk.url : `https://${lnk.url}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1.5 text-blue-600 dark:text-blue-400 hover:underline truncate max-w-[280px]"
                        >
                          <ExternalLink className="h-3 w-3 shrink-0" />
                          <span className="truncate font-medium">{lnk.label || lnk.url}</span>
                        </a>
                        <button
                          onClick={() => handleRemoveCustomLink(lnk.id)}
                          className="opacity-0 group-hover:opacity-100 p-0.5 text-muted-foreground hover:text-destructive transition-opacity"
                          title="Remove link"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Custom Pages Attached */}
              {customPages.length > 0 && (
                <div className="space-y-1.5 pt-1">
                  <span className="text-[11px] font-semibold text-muted-foreground flex items-center gap-1.5">
                    <FileText className="h-3 w-3 text-purple-500" /> Documents & Pages
                  </span>
                  <div className="space-y-1">
                    {customPages.map((pg) => (
                      <div
                        key={pg.id}
                        className="flex items-center justify-between p-1.5 rounded-lg border border-border/60 bg-muted/20 text-xs group"
                      >
                        <div className="flex items-center gap-1.5 text-foreground font-medium truncate">
                          <FileText className="h-3 w-3 text-purple-500 shrink-0" />
                          <span className="truncate">{pg.title}</span>
                        </div>
                        <button
                          onClick={() => setCustomPages((prev) => prev.filter((p) => p.id !== pg.id))}
                          className="opacity-0 group-hover:opacity-100 p-0.5 text-muted-foreground hover:text-destructive transition-opacity"
                          title="Remove page"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

            {/* CRM & Contact Info */}
            {visibleSections.crm && (
              <div className="pt-4 border-t border-border/60 space-y-3">

                {/* Client Account */}
                <div className="flex items-center gap-2">
                  <Building2 className="h-4 w-4 text-emerald-500 shrink-0" />
                  <input
                    type="text"
                    value={client}
                    onChange={(e) => setClient(e.target.value)}
                    onBlur={() => persistChanges({ client })}
                    className="w-full text-xs font-medium text-foreground bg-transparent border-b border-transparent hover:border-border focus:border-primary focus:outline-none py-1"
                    placeholder="Account / Client"
                  />
                </div>

                {/* Contact Person */}
                <div className="flex items-center gap-2">
                  <User className="h-4 w-4 text-sky-500 shrink-0" />
                  <input
                    type="text"
                    value={contactPerson}
                    onChange={(e) => setContactPerson(e.target.value)}
                    onBlur={() => persistChanges({ contactPerson })}
                    className="w-full text-xs font-medium text-foreground bg-transparent border-b border-transparent hover:border-border focus:border-primary focus:outline-none py-1"
                    placeholder="Key Contact"
                  />
                </div>

                {/* Designation */}
                <div className="flex items-center gap-2">
                  <User className="h-4 w-4 text-amber-500 shrink-0" />
                  <input
                    type="text"
                    value={contactDesignation}
                    onChange={(e) => setContactDesignation(e.target.value)}
                    onBlur={() => persistChanges({ contactDesignation })}
                    className="w-full text-xs font-medium text-foreground bg-transparent border-b border-transparent hover:border-border focus:border-primary focus:outline-none py-1"
                    placeholder="Designation"
                  />
                </div>

                {/* Email */}
                <div className="flex items-center gap-2">
                  <Mail className="h-4 w-4 text-blue-500 shrink-0" />
                  <input
                    type="email"
                    value={contactEmail}
                    onChange={(e) => setContactEmail(e.target.value)}
                    onBlur={() => persistChanges({ contactEmail })}
                    className="w-full text-xs font-medium text-foreground bg-transparent border-b border-transparent hover:border-border focus:border-primary focus:outline-none py-1"
                    placeholder="Contact Email"
                  />
                </div>

                {/* Phone */}
                <div className="flex items-center gap-2">
                  <Phone className="h-4 w-4 text-green-500 shrink-0" />
                  <input
                    type="text"
                    value={contactPhone}
                    onChange={(e) => setContactPhone(e.target.value)}
                    onBlur={() => persistChanges({ contactPhone })}
                    className="w-full text-xs font-medium text-foreground bg-transparent border-b border-transparent hover:border-border focus:border-primary focus:outline-none py-1"
                    placeholder="Contact Phone"
                  />
                </div>

                {/* Source */}
                <div className="flex items-center gap-2">
                  <Layers className="h-4 w-4 text-purple-500 shrink-0" />
                  <input
                    type="text"
                    value={source}
                    onChange={(e) => setSource(e.target.value)}
                    onBlur={() => persistChanges({ source })}
                    className="w-full text-xs font-medium text-foreground bg-transparent border-b border-transparent hover:border-border focus:border-primary focus:outline-none py-1"
                    placeholder="Deal Source"
                  />
                </div>

                {/* Client Address */}
                <div className="flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-rose-500 shrink-0" />
                  <input
                    type="text"
                    value={clientAddress}
                    onChange={(e) => setClientAddress(e.target.value)}
                    onBlur={() => persistChanges({ clientAddress })}
                    className="w-full text-xs font-medium text-foreground bg-transparent border-b border-transparent hover:border-border focus:border-primary focus:outline-none py-1"
                    placeholder="Client Address"
                  />
                </div>

                {/* Client GSTIN */}
                <div className="flex items-center gap-2">
                  <FileText className="h-4 w-4 text-orange-500 shrink-0" />
                  <input
                    type="text"
                    value={clientGstin}
                    onChange={(e) => setClientGstin(e.target.value.toUpperCase())}
                    onBlur={() => persistChanges({ clientGstin })}
                    className="w-full text-xs font-medium text-foreground bg-transparent border-b border-transparent hover:border-border focus:border-primary focus:outline-none py-1 uppercase"
                    placeholder="Client GSTIN"
                  />
                </div>

                {/* Customer state: the place of supply when the customer has no GSTIN (the GSTIN wins when it has one) */}
                <div className="flex items-center gap-2">
                  <FileText className="h-4 w-4 text-orange-500 shrink-0" />
                  <select
                    value={resolvePlaceOfSupply({ customerGstin: clientGstin, customerState: clientState }).label}
                    disabled={stateCodeFromGstin(clientGstin) !== null}
                    onChange={(e) => { setClientState(e.target.value); persistChanges({ clientState: e.target.value }) }}
                    title={stateCodeFromGstin(clientGstin) !== null ? 'Taken from the customer GSTIN' : 'Customer state — decides CGST + SGST or IGST'}
                    className="w-full text-xs font-medium text-foreground bg-transparent border-b border-transparent hover:border-border focus:border-primary focus:outline-none py-1"
                  >
                    <option value="">Customer state (for GST)…</option>
                    {GST_STATES.map((st) => (
                      <option key={st.code} value={st.label}>{st.label}</option>
                    ))}
                  </select>
                </div>

                {/* Add Link / Page Field */}
                <div className="flex items-center gap-2 pt-2">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button className="flex items-center gap-1.5 px-2 py-1 rounded text-xs font-semibold cursor-pointer border border-border/50 hover:border-border transition-colors text-muted-foreground hover:text-foreground">
                        <Plus className="h-3.5 w-3.5" /> Add Link / Page
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start" className="w-48 p-1">
                      <DropdownMenuSub>
                        <DropdownMenuSubTrigger className="flex items-center gap-2 text-xs py-2 cursor-pointer font-medium">
                          <LinkIcon className="h-4 w-4 text-muted-foreground" />
                          <span>Link</span>
                        </DropdownMenuSubTrigger>
                        <DropdownMenuSubContent className="p-3 w-64 bg-popover border border-border shadow-2xl rounded-xl space-y-3">
                          <div className="space-y-1">
                            <label className="text-xs font-semibold text-muted-foreground">URL</label>
                            <Input
                              value={newLinkUrl}
                              onChange={(e) => setNewLinkUrl(e.target.value)}
                              placeholder="https://"
                              className="h-8 text-xs bg-background"
                            />
                          </div>
                          <div className="space-y-1">
                            <label className="text-xs font-semibold text-muted-foreground">Label</label>
                            <Input
                              value={newLinkLabel}
                              onChange={(e) => setNewLinkLabel(e.target.value)}
                              placeholder="e.g. Sales Deck / Spec"
                              className="h-8 text-xs bg-background"
                            />
                          </div>
                          <Button
                            type="button"
                            size="sm"
                            className="w-full h-8 text-xs bg-blue-600 hover:bg-blue-700 text-white font-medium"
                            onClick={() => handleAddCustomLink()}
                          >
                            Add
                          </Button>
                        </DropdownMenuSubContent>
                      </DropdownMenuSub>
                      <DropdownMenuItem
                        onClick={handleAddCustomPage}
                        className="cursor-pointer flex items-center gap-2 text-xs py-2 font-medium"
                      >
                        <FileText className="h-4 w-4 text-muted-foreground" />
                        <span>Page</span>
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>

    {/* Create Deal Reminder Modal */}
    <Dialog open={isDealReminderModalOpen} onOpenChange={setIsDealReminderModalOpen}>
      <DialogContent className="max-w-md bg-card border-border shadow-2xl rounded-2xl p-6">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base font-bold text-foreground">
            <Bell className="h-5 w-5 text-amber-500" /> Create Deal Reminder
          </DialogTitle>
          <p className="text-xs text-muted-foreground mt-1">
            Set a reminder that appears directly in your GoodDay notification bell.
          </p>
        </DialogHeader>

        <div className="space-y-4 pt-2">
          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-foreground">Reminder Title</label>
            <Input
              value={dealReminderTitle}
              onChange={(e) => setDealReminderTitle(e.target.value)}
              placeholder={`Follow up on ${title || deal?.title || 'this deal'}`}
              className="text-xs"
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-foreground">When</label>
            <div className="grid grid-cols-2 gap-2">
              {[
                { id: 'tomorrow_morning', label: 'Tomorrow 9:00 AM' },
                { id: 'in_2_days', label: 'In 2 Days' },
                { id: 'next_week', label: 'Next Week' },
                { id: 'custom', label: 'Custom Date & Time' },
              ].map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => setDealReminderPreset(p.id as any)}
                  className={`p-2 rounded-xl border text-xs font-medium text-left transition-all ${
                    dealReminderPreset === p.id
                      ? 'border-primary bg-primary/10 text-primary font-bold shadow-xs'
                      : 'border-border bg-card text-muted-foreground hover:bg-muted/60'
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>
            {dealReminderPreset === 'custom' && (
              <Input
                type="datetime-local"
                value={dealReminderCustomDate}
                onChange={(e) => setDealReminderCustomDate(e.target.value)}
                className="text-xs mt-2"
              />
            )}
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-foreground">Notes (Optional)</label>
            <Textarea
              value={dealReminderNotes}
              onChange={(e) => setDealReminderNotes(e.target.value)}
              placeholder="e.g. Follow up on proposal, check client feedback..."
              className="text-xs min-h-[70px] resize-none"
            />
          </div>

          <div className="flex items-center justify-end gap-2 pt-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsDealReminderModalOpen(false)}
              disabled={isCreatingDealReminder}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={handleSaveDealReminder}
              disabled={isCreatingDealReminder || !dealReminderTitle.trim()}
              className="bg-amber-600 hover:bg-amber-700 text-white font-semibold flex items-center gap-1.5 shadow-md"
            >
              <Bell className="h-3.5 w-3.5" />
              {isCreatingDealReminder ? 'Saving...' : 'Set Reminder'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
    <ConfirmDialog
      open={isDeleteOpen}
      onCancel={() => setIsDeleteOpen(false)}
      onConfirm={handleDelete}
      loading={isDeleting}
      danger
      confirmLabel={mayDeleteAlone ? 'Delete permanently' : 'Send for approval'}
      title={
        mayDeleteAlone
          ? `Delete "${deal?.title || 'this deal'}"?`
          : `Ask the superadmin to delete "${deal?.title || 'this deal'}"?`
      }
      message={
        mayDeleteAlone
          ? 'This removes the deal from the database for good, along with the notes on it. Quotations, invoices, the launched project and timesheets created from it are kept. Use Archive deal to keep the deal itself.'
          : 'Nothing is deleted yet. The superadmin is asked to approve this, and only after that approval is the deal — with its notes — removed. Quotations, invoices, the launched project and timesheets created from it are kept.'
      }
    >
      {!mayDeleteAlone && (
        <div className="space-y-1.5">
          <label className="text-xs font-medium text-muted-foreground">
            Reason — the superadmin reads this
          </label>
          <Textarea
            rows={3}
            value={deleteReason}
            onChange={(e) => setDeleteReason(e.target.value)}
            placeholder="Lost to a competitor, duplicate of another deal, the client pulled out…"
            className="text-sm"
          />
        </div>
      )}
    </ConfirmDialog>
    </>
  )
}
